import React, { useState, useEffect } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { TacosThemeModal } from '../Games/TacosThemeModal';

export default function EventsManager({ getOutOfTheDynamic }) {
  const { authenticatedFetch } = useAuth();
  const { showMessage } = useFloatMessage();
  
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [showTacosThemeModal, setShowTacosThemeModal] = useState(false);
  
  // Dynamics list & config states
  const [gameTemplates, setGameTemplates] = useState([]);
  const [gameConfigs, setGameConfigs] = useState([]);

  // Form states
  const [newEventNombre, setNewEventNombre] = useState('');
  const [newEventFecha, setNewEventFecha] = useState(new Date().toISOString().substring(0, 16));
  const [newEventStatus, setNewEventStatus] = useState('Active');
  
  const [newRoomNombre, setNewRoomNombre] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [isDefaultRoom, setIsDefaultRoom] = useState(false);
  const [zoneLists, setZoneLists] = useState([]);
  const [selectedZoneListId, setSelectedZoneListId] = useState('');

  // Editing Room states
  const [editingRoomId, setEditingRoomId] = useState(null);

  // Confirm Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);

  // Loading states
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

  const openConfirmationModal = (messageToDisplay, actionFunctionToExecute, yesButtonText) => {
    setConfirmModalData({
      message: messageToDisplay,
      actionToYesButton: actionFunctionToExecute,
      textYesButton: yesButtonText || 'Sí, confirmar',
      textNoButton: 'Cancelar'
    });
    setShowConfirmModal(true);
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/events`);
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      } else {
        console.error("Failed to fetch events", res.statusText);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchRooms = async (eventId) => {
    setLoadingRooms(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/events/${eventId}/rooms`);
      if (res.ok) {
        const data = await res.json();
        setRooms(data);
      } else {
        console.error("Failed to fetch rooms", res.statusText);
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
    } finally {
      setLoadingRooms(false);
    }
  };

  const fetchGameTemplates = async () => {
    try {
      const res = await authenticatedFetch(`${API_URL}v2/gametemplates`);
      if (res.ok) {
        const data = await res.json();
        setGameTemplates(data);
      } else {
        console.error("Failed to fetch game templates", res.statusText);
      }
    } catch (error) {
      console.error("Error fetching game templates:", error);
    }
  };

  const fetchGameConfigs = async () => {
    try {
      const res = await authenticatedFetch(`${API_URL}v2/gameconfigs`);
      if (res.ok) {
        const data = await res.json();
        setGameConfigs(data);
      } else {
        console.error("Failed to fetch game configs", res.statusText);
      }
    } catch (error) {
      console.error("Error fetching game configs:", error);
    }
  };

  const fetchZoneLists = async () => {
    try {
      const res = await authenticatedFetch(`${API_URL}v2/zonelists`);
      if (res.ok) {
        const data = await res.json();
        setZoneLists(data);
      } else {
        console.error("Failed to fetch zone lists", res.statusText);
      }
    } catch (error) {
      console.error("Error fetching zone lists:", error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchGameTemplates();
    fetchGameConfigs();
    fetchZoneLists();
  }, []);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setNewEventNombre(event.nombre);
    setNewEventFecha(new Date(event.fecha).toISOString().substring(0, 16));
    setNewEventStatus(event.status);
    fetchRooms(event.id);
    handleCancelEditRoom();
  };

  const handleCancelEditEvent = () => {
    setSelectedEvent(null);
    setNewEventNombre('');
    setNewEventFecha(new Date().toISOString().substring(0, 16));
    setNewEventStatus('Active');
    setRooms([]);
    handleCancelEditRoom();
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!newEventNombre.trim()) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor ingresa el nombre del evento." });
      return;
    }

    setActionInProgress(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: newEventNombre.trim(),
          fecha: new Date(newEventFecha).toISOString(),
          status: newEventStatus
        })
      });

      if (res.ok) {
        const newEv = await res.json();
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "¡Evento creado correctamente!" });
        setNewEventNombre('');
        setNewEventFecha(new Date().toISOString().substring(0, 16));
        setNewEventStatus('Active');
        fetchEvents();
        handleSelectEvent(newEv);
      } else {
        const errMsg = await res.text();
        showMessage({ type: MESSAGE_TYPE.BAD, text: `Error al crear evento: ${errMsg}` });
      }
    } catch (error) {
      console.error("Error creating event:", error);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error de conexión al crear evento." });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    if (!newEventNombre.trim()) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor ingresa el nombre del evento." });
      return;
    }

    setActionInProgress(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: newEventNombre.trim(),
          fecha: new Date(newEventFecha).toISOString(),
          status: newEventStatus
        })
      });

      if (res.ok) {
        const updatedEv = await res.json();
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "¡Evento actualizado correctamente!" });
        fetchEvents();
        setSelectedEvent(updatedEv);
      } else {
        const errMsg = await res.text();
        showMessage({ type: MESSAGE_TYPE.BAD, text: `Error al actualizar evento: ${errMsg}` });
      }
    } catch (error) {
      console.error("Error updating event:", error);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error de conexión al actualizar evento." });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteEvent = () => {
    if (!selectedEvent) return;

    const executeDelete = async () => {
      try {
        const res = await authenticatedFetch(`${API_URL}v2/events/${selectedEvent.id}`, {
          method: 'DELETE'
        });

        if (res.ok) {
          showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "¡Evento eliminado correctamente!" });
          handleCancelEditEvent();
          fetchEvents();
          return { success: true };
        } else {
          const errMsg = await res.text();
          return { success: false, message: `Error al eliminar evento: ${errMsg}` };
        }
      } catch (error) {
        console.error("Error deleting event:", error);
        return { success: false, message: "Error de conexión al intentar eliminar el evento." };
      }
    };

    openConfirmationModal(
      `¿Estás seguro de eliminar el evento "${selectedEvent.nombre}"? Esto ocultará el evento y todas sus salas, expulsando a todos los usuarios conectados.`,
      executeDelete,
      "Sí, eliminar evento"
    );
  };

  const handleStartEditRoom = (room) => {
    setEditingRoomId(room.id);
    setNewRoomNombre(room.nombreSala);
    setSelectedTemplateId(room.gameTemplateId || '');
    setSelectedConfigId(room.configId || '');
    setIsDefaultRoom(room.isDefault || false);
    setSelectedZoneListId(room.zoneListId || '');
  };

  const handleCancelEditRoom = () => {
    setEditingRoomId(null);
    setNewRoomNombre('');
    setSelectedTemplateId('');
    setSelectedConfigId('');
    setIsDefaultRoom(false);
    setSelectedZoneListId('');
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    if (!newRoomNombre.trim()) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor ingresa el nombre de la sala." });
      return;
    }
    if (!isDefaultRoom) {
      if (!selectedTemplateId) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor selecciona una plantilla de juego." });
        return;
      }
      if (!selectedConfigId) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor selecciona una configuración." });
        return;
      }
    }
    if (!selectedZoneListId) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor selecciona un listado de zonas." });
      return;
    }

    setActionInProgress(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: selectedEvent.id,
          nombreSala: newRoomNombre.trim(),
          gameTemplateId: selectedTemplateId || null,
          configId: selectedConfigId || null,
          isDefault: isDefaultRoom,
          zoneListId: selectedZoneListId
        })
      });

      if (res.ok) {
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "¡Sala creada correctamente!" });
        setNewRoomNombre('');
        setSelectedTemplateId('');
        setSelectedConfigId('');
        setIsDefaultRoom(false);
        setSelectedZoneListId('');
        fetchRooms(selectedEvent.id);
      } else {
        const errMsg = await res.text();
        showMessage({ type: MESSAGE_TYPE.BAD, text: `Error al crear sala: ${errMsg}` });
      }
    } catch (error) {
      console.error("Error creating room:", error);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error de conexión al crear sala." });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    if (!editingRoomId || !selectedEvent) return;
    if (!newRoomNombre.trim()) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor ingresa el nombre de la sala." });
      return;
    }
    if (!isDefaultRoom) {
      if (!selectedTemplateId) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor selecciona una plantilla de juego." });
        return;
      }
      if (!selectedConfigId) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor selecciona una configuración." });
        return;
      }
    }
    if (!selectedZoneListId) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Por favor selecciona un listado de zonas." });
      return;
    }

    setActionInProgress(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/rooms/${editingRoomId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombreSala: newRoomNombre.trim(),
          gameTemplateId: selectedTemplateId || null,
          configId: selectedConfigId || null,
          isDefault: isDefaultRoom,
          zoneListId: selectedZoneListId
        })
      });

      if (res.ok) {
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "¡Sala actualizada correctamente!" });
        handleCancelEditRoom();
        fetchRooms(selectedEvent.id);
      } else {
        const errMsg = await res.text();
        showMessage({ type: MESSAGE_TYPE.BAD, text: `Error al actualizar sala: ${errMsg}` });
      }
    } catch (error) {
      console.error("Error updating room:", error);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error de conexión al actualizar sala." });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteRoom = (room) => {
    const executeDelete = async () => {
      try {
        const res = await authenticatedFetch(`${API_URL}v2/rooms/${room.id}`, {
          method: 'DELETE'
        });

        if (res.ok) {
          showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "¡Sala eliminada correctamente!" });
          fetchRooms(selectedEvent.id);
          if (editingRoomId === room.id) {
            handleCancelEditRoom();
          }
          return { success: true };
        } else {
          const errMsg = await res.text();
          return { success: false, message: `Error al eliminar sala: ${errMsg}` };
        }
      } catch (error) {
        console.error("Error deleting room:", error);
        return { success: false, message: "Error de conexión al intentar eliminar la sala." };
      }
    };

    openConfirmationModal(
      `¿Estás seguro de eliminar la sala "${room.nombreSala}"? Se expulsará a todos los aficionados y pantallas sintonizadas.`,
      executeDelete,
      "Sí, eliminar sala"
    );
  };

  // Filter game configurations corresponding to the selected template
  const filteredConfigs = gameConfigs.filter(cfg => cfg.templateId === selectedTemplateId);

  return (
    <SecondaryBase actionToExecute={getOutOfTheDynamic}>
      <div className="events-manager-view">
        <header className="events-manager-header d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="fw-black"><i className="bi bi-calendar-event-fill textADSL--Red"></i> Gestión de Eventos y Salas</h1>
            <p className="text-muted mb-0">Migración SaaS V2: Configuración jerárquica de Eventos y Salas de Interaktia.</p>
          </div>
          <div>
            <button 
              type="button" 
              className="btn btn-outline-primary btn-sm me-2"
              onClick={() => setShowTacosThemeModal(true)}
            >
              <i className="bi bi-palette-fill me-1"></i> Temas (Hamburguesas / Tacos)
            </button>
            <a href="/admin/zones-lists" className="btn btn-outline-info btn-sm me-2">
              <i className="bi bi-geo-alt me-1"></i> Listados de Zonas
            </a>
            <a href="/admin/trivias" className="btn btn-outline-success btn-sm me-2">
              <i className="bi bi-file-earmark-ruled me-1"></i> Cuestionarios de Trivias
            </a>
            <a href="/admin/operators" className="btn btn-outline-warning btn-sm">
              <i className="bi bi-shield-lock me-1"></i> Operadores & Seguridad
            </a>
          </div>
        </header>

        <div className="events-manager-grid">
          {/* PANEL IZQUIERDO: EVENTOS */}
          <div className="events-section card--glass">
            <h2 className="section-title"><i className="bi bi-list-stars"></i> Eventos Existentes</h2>
            
            {loadingEvents ? (
              <div className="loader-container">
                <div className="spinner-border text-danger" role="status"></div>
                <p>Cargando eventos...</p>
              </div>
            ) : events.length === 0 ? (
              <p className="empty-message">No hay eventos registrados. Crea el primero a la derecha.</p>
            ) : (
              <ul className="events-list">
                {events.map((ev) => (
                  <li 
                    key={ev.id} 
                    className={`event-item ${selectedEvent?.id === ev.id ? 'active' : ''}`}
                    onClick={() => handleSelectEvent(ev)}
                  >
                    <div className="event-item-info">
                      <span className="event-name">{ev.nombre}</span>
                      <span className="event-date">
                        <i className="bi bi-clock"></i> {new Date(ev.fecha).toLocaleString()}
                      </span>
                    </div>
                    <span className={`status-badge status--${ev.status.toLowerCase()}`}>
                      {ev.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* PANEL CENTRAL: CREACIÓN / EDICIÓN DE EVENTO */}
          <div className="create-section card--glass">
            {selectedEvent ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="section-title mb-0"><i className="bi bi-pencil-square"></i> Editar Evento</h2>
                  <button 
                    type="button"
                    className="btn btn-sm btn-link textADSL--Red fw-semibold p-0" 
                    onClick={handleCancelEditEvent}
                  >
                    <i className="bi bi-plus-circle"></i> Nuevo Evento
                  </button>
                </div>
                <form onSubmit={handleUpdateEvent} className="events-form">
                  <div className="form-group mb-3">
                    <label className="form-label">Nombre del Evento</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="Ej. Clásico Regio 2026"
                      value={newEventNombre}
                      onChange={(e) => setNewEventNombre(e.target.value)}
                      disabled={actionInProgress}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Fecha y Hora</label>
                    <input 
                      type="datetime-local" 
                      className="form-input"
                      value={newEventFecha}
                      onChange={(e) => setNewEventFecha(e.target.value)}
                      disabled={actionInProgress}
                    />
                  </div>

                  <div className="form-group mb-4">
                    <label className="form-label">Estado</label>
                    <select 
                      className="form-input"
                      value={newEventStatus}
                      onChange={(e) => setNewEventStatus(e.target.value)}
                      disabled={actionInProgress}
                    >
                      <option value="Active">Activo</option>
                      <option value="Inactive">Inactivo</option>
                      <option value="Completed">Completado</option>
                    </select>
                  </div>

                  <button 
                    type="submit" 
                    className="Btn__Adsl--Red fw-bold w-100 mb-2"
                    disabled={actionInProgress}
                  >
                    {actionInProgress ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                  
                  <button 
                    type="button" 
                    className="Btn__Adsl--RedOutline w-100 fw-bold"
                    onClick={handleDeleteEvent}
                    disabled={actionInProgress}
                  >
                    <i className="bi bi-trash-fill"></i> Eliminar Evento
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="section-title"><i className="bi bi-calendar-plus"></i> Crear Evento</h2>
                <form onSubmit={handleCreateEvent} className="events-form">
                  <div className="form-group mb-3">
                    <label className="form-label">Nombre del Evento</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="Ej. Clásico Regio 2026"
                      value={newEventNombre}
                      onChange={(e) => setNewEventNombre(e.target.value)}
                      disabled={actionInProgress}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Fecha y Hora</label>
                    <input 
                      type="datetime-local" 
                      className="form-input"
                      value={newEventFecha}
                      onChange={(e) => setNewEventFecha(e.target.value)}
                      disabled={actionInProgress}
                    />
                  </div>

                  <div className="form-group mb-4">
                    <label className="form-label">Estado Inicial</label>
                    <select 
                      className="form-input"
                      value={newEventStatus}
                      onChange={(e) => setNewEventStatus(e.target.value)}
                      disabled={actionInProgress}
                    >
                      <option value="Active">Activo</option>
                      <option value="Inactive">Inactivo</option>
                      <option value="Completed">Completado</option>
                    </select>
                  </div>
                  <button 
                    type="submit" 
                    className="Btn__Adsl--Red fw-bold w-100"
                    disabled={actionInProgress}
                  >
                    {actionInProgress ? 'Guardando...' : 'Crear Evento'}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* PANEL DERECHO: SALAS (CONDICIONAL AL SELECCIONAR UN EVENTO) */}
          <div className="rooms-section card--glass">
            {selectedEvent ? (
              <>
                <h2 className="section-title">
                  <i className="bi bi-door-open"></i> Salas de: <span className="textADSL--Red">{selectedEvent.nombre}</span>
                </h2>

                <div className="rooms-container">
                  <div className="rooms-list-container">
                    {loadingRooms ? (
                      <div className="loader-container">
                        <div className="spinner-border text-danger" role="status"></div>
                      </div>
                    ) : rooms.length === 0 ? (
                      <p className="empty-message">No hay salas creadas para este evento.</p>
                    ) : (
                      <ul className="rooms-list">
                        {rooms.map((room) => (
                          <li key={room.id} className="room-item d-flex justify-content-between align-items-center">
                            <div className="room-info">
                              <span className="room-name">
                                {room.nombreSala}
                                {room.isDefault && (
                                  <span className="badge bg-success text-white ms-2">
                                    <i className="bi bi-globe"></i> GLOBAL
                                  </span>
                                )}
                              </span>
                              <div className="room-details mt-1">
                                {room.gameTemplateName && (
                                  <span className="badge bg-secondary me-2">{room.gameTemplateName.toUpperCase()}</span>
                                )}
                                {room.configName && (
                                  <span className="badge bg-light text-dark border me-2">{room.configName}</span>
                                )}
                                {room.zoneListName && (
                                  <span className="badge bg-info text-white"><i className="bi bi-geo-alt"></i> {room.zoneListName.toUpperCase()}</span>
                                )}
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <button 
                                type="button"
                                className="btn btn-sm btn-outline-secondary me-1 py-0 px-2"
                                onClick={() => handleStartEditRoom(room)}
                                title="Editar sala"
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                              <button 
                                type="button"
                                className="btn btn-sm btn-outline-danger me-2 py-0 px-2"
                                onClick={() => handleDeleteRoom(room)}
                                title="Eliminar sala"
                              >
                                <i className="bi bi-trash-fill"></i>
                              </button>
                              <span className="room-id-badge">{room.id.substring(0, 8)}...</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <form onSubmit={editingRoomId ? handleUpdateRoom : handleCreateRoom} className="rooms-form mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h3 className="sub-title mb-0">
                        <i className={editingRoomId ? "bi bi-pencil-square" : "bi bi-plus-circle"}></i> {editingRoomId ? "Editar Sala" : "Agregar Sala"}
                      </h3>
                      {editingRoomId && (
                        <button 
                          type="button" 
                          className="btn btn-sm btn-link text-secondary p-0" 
                          onClick={handleCancelEditRoom}
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                    <div className="form-group mb-3">
                      <label className="form-label">Nombre de Sala</label>
                      <input 
                        type="text" 
                        className="form-input" 
                        placeholder="Ej. Stand Principal Norte"
                        value={newRoomNombre}
                        onChange={(e) => setNewRoomNombre(e.target.value)}
                        disabled={actionInProgress}
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label className="form-label">Plantilla de Juego (Obligatorio)</label>
                      <select
                        className="form-select form-input"
                        value={selectedTemplateId}
                        onChange={(e) => {
                          setSelectedTemplateId(e.target.value);
                          setSelectedConfigId('');
                        }}
                        disabled={actionInProgress}
                      >
                        <option value="">-- Seleccionar plantilla --</option>
                        {gameTemplates.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.nombreIdentificador.toUpperCase()} ({t.tipoJuego})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label className="form-label">Configuración (Obligatorio)</label>
                      <select
                        className="form-select form-input"
                        value={selectedConfigId}
                        onChange={(e) => setSelectedConfigId(e.target.value)}
                        disabled={actionInProgress || !selectedTemplateId}
                      >
                        <option value="">-- Seleccionar configuración --</option>
                        {filteredConfigs.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.nombreConfiguracion}
                          </option>
                        ))}
                      </select>
                      {(gameTemplates.find(t => t.id === selectedTemplateId)?.nombreIdentificador?.toLowerCase() === 'tacos' || selectedTemplateId === 'd0e1f2a3-b4c5-6d7e-8f9a-0b1c2d3e4f5a') && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm mt-2 w-100 fw-bold shadow-sm"
                          onClick={() => setShowTacosThemeModal(true)}
                        >
                          <i className="bi bi-palette-fill me-1"></i> Personalizar Temas (Hamburguesas / Tacos)
                        </button>
                      )}
                    </div>

                    <div className="form-group mb-4">
                      <label className="form-label">Listado de Zonas (Obligatorio)</label>
                      <select
                        className="form-select form-input"
                        value={selectedZoneListId}
                        onChange={(e) => setSelectedZoneListId(e.target.value)}
                        disabled={actionInProgress}
                      >
                        <option value="">-- Seleccionar listado de zonas --</option>
                        {zoneLists.map(zl => (
                          <option key={zl.id} value={zl.id}>
                            {zl.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-check mb-4 text-start">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="chkIsDefault"
                        checked={isDefaultRoom}
                        onChange={(e) => setIsDefaultRoom(e.target.checked)}
                        disabled={actionInProgress}
                      />
                      <label className="form-check-label text-dark fw-semibold" htmlFor="chkIsDefault">
                        ¿Establecer como sala global/por defecto?
                      </label>
                    </div>

                    <button 
                      type="submit" 
                      className="Btn__Adsl--RedOutlineTransparent fw-bold w-100"
                      disabled={actionInProgress || !newRoomNombre.trim() || !selectedZoneListId || (!isDefaultRoom && (!selectedTemplateId || !selectedConfigId))}
                    >
                      {actionInProgress ? 'Guardando...' : (editingRoomId ? 'Guardar Cambios' : 'Crear Sala')}
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="no-event-selected">
                <i className="bi bi-arrow-left-circle-fill textADSL--Red"></i>
                <p>Selecciona un evento de la lista para ver y gestionar sus salas.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showConfirmModal && (
        <Confirm 
          message={confirmModalData?.message}
          actionToYesButton={confirmModalData?.actionToYesButton}
          textYesButton={confirmModalData?.textYesButton}
          textNoButton={confirmModalData?.textNoButton}
          setShowModal={setShowConfirmModal}
          setConfirmData={setConfirmModalData} 
        />
      )}
      <TacosThemeModal
        show={showTacosThemeModal}
        onClose={() => setShowTacosThemeModal(false)}
        onSave={fetchGameConfigs}
      />
    </SecondaryBase>
  );
}
